import { initializeCompanionUpdate, closeUpdateOverlay, isCompanionUpdateOverlayOpen, handleUpdateAction, selectMindTheme, selectMindCandidate, setCircleDraft, changeMindConsent } from "./companionUpdate";
import {
  onAuthStateChanged,
  getRedirectResult,
  signInWithRedirect,
} from "firebase/auth";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { SpeechRecognition as NativeSpeechRecognition } from "@capgo/capacitor-speech-recognition";
import { auth } from "./firebase";
import { pathForTab, tabFromPath } from "./routing";
import { TAB_ORDER, TAB_LABELS } from "./constants";
import { state } from "./state";
import { showToast, getFieldValue } from "./utils";
import {
  registerServices,
  signInWithGoogle,
  signInWithApple,
  signInWithEmail,
  resetEmailPassword,
  runSignOut,
  hydrateAppData,
  fetchFeed,
  fetchPublicFeedAsGuest,
  createSharedPost,
  deleteSharedPost,
  hideSharedPost,
  reportSharedPost,
  blockSharedPostAuthor,
  tapResonance,
  toggleSavedPost,
  sharePost,
  openSharedThread,
  openCommunityThread,
  closeThreadModal,
  createReply,
  deleteReply,
  reportReply,
  searchUsers,
  openPublicProfile,
  closePublicProfile,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  removeFriend,
  fetchCommunities,
  createCommunity,
  openCommunity,
  fetchCommunityPosts,
  createCommunityPost,
  deleteCommunityPost,
  deleteCommunity,
  joinCommunity,
  joinByCode,
  leaveCommunity,
  acceptInvite,
  denyInvite,
  inviteByUsername,
  removeCommunityMember,
  updateJoinCodeSettings,
  saveNote,
  deleteNote,
  refreshVisibleNoteInsights,
  fetchThinkingTimeline,
  ensureNoteSimilarThoughts,
  fetchActivity,
  markActivityRead,
  openActivity,
  saveNotificationPreference,
  saveAIPreferences,
  saveSimilarThoughtFeedback,
  openCompanionSession,
  closeCompanionSession,
  finishCompanionSession,
  editCompanionMemory,
  keepTalking,
  sendCompanionMessage,
  setCompanionMode,
  setCompanionIntent,
  prepareCompanionMemory,
  savePreparedCompanionMemory,
  dismissCompanionMemoryOffer,
  prepareCompanionJournal,
  updateCompanionJournalDraft,
  discardCompanionJournalDraft,
  saveCompanionJournalDraft,
  loadCompanionContext,
  updateCompanionPortraitGuidance,
  deleteCompanionMemory,
  deleteCompanionPortrait,
  setupCrossPlatformEncryption,
  unlockCrossPlatformEncryption,
  changeCrossPlatformEncryptionPassphrase,
  forgetRememberedCrossPlatformEncryption,
  deleteAccountFlow,
  saveUsername,
  saveProfileSettings,
  saveNativePushToken,
  startThoughtMatching,
  stopThoughtMatching,
  approveThoughtMatchExcerpt,
  dismissThoughtMatchExcerpt,
  respondToThoughtMatch,
  openThoughtMatchConversation,
  closeThoughtMatchConversation,
  sendThoughtMatchMessage,
  leaveThoughtMatchConversation,
  reportThoughtMatchConversation, loadDiscoveryShelf, loadSavedPostDetails } from "./services";
import { erasePortrait, handlePortraitAction, loadPortrait, performPortraitAction, refreshPortraitAvailability, refreshPortraitConsent, resetPortraitState } from "./portrait";
import { handlePortraitMatchingAction, resetPortraitMatching, setPortraitMatchConsent, startPortraitMatching } from "./portraitMatching";
import { render as renderUI, readAcknowledgedPatternIds, COMPANION_ACK_STORAGE_KEY } from "./ui";
import type { TabName } from "./types";

// ── Render + busy ─────────────────────────────────────────────────────────
let _lastRenderedTab = "";
let feedLoadObserver: IntersectionObserver | null = null;

type ActiveFieldSnapshot = {
  selector: string;
  value?: string;
  selectionStart?: number | null;
  selectionEnd?: number | null;
  tab: TabName;
};

type ScrollSnapshot = {
  tab: TabName;
  overlayKey: string;
  windowY: number;
  scrollables: Array<{
    key: string;
    top: number;
    left: number;
  }>;
};

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&");
}

function currentOverlayKey(): string {
  if (state.threadModal) {
    return `thread:${state.threadModal.scope}:${state.threadModal.post.id}`;
  }
  if (state.companionSession) {
    return `companion:${state.companionSession.sessionId}`;
  }
  if (state.publicProfile) {
    return `profile:${state.publicProfile.uid}`;
  }
  if (state.selectedMatchConversationId) {
    return `match:${state.selectedMatchConversationId}`;
  }
  if (state.selectedMatchExcerpt) {
    return "match-excerpt";
  }
  if (state.thoughtMatchExcerptDraft) {
    return "match-draft";
  }
  if (state.communityOverlay) {
    return "community-overlay";
  }
  if (state.confirmModal) {
    return "confirm";
  }
  if (state.thinkingTimeline.selectedPatternId) {
    return `pattern:${state.thinkingTimeline.selectedPatternId}`;
  }
  return "";
}

function captureScrollSnapshot(): ScrollSnapshot {
  const scrollables = Array.from(document.querySelectorAll<HTMLElement>("[data-scroll-preserve]"))
    .map((element) => ({
      key: element.dataset["scrollPreserve"] || "",
      top: element.scrollTop,
      left: element.scrollLeft,
    }))
    .filter((entry) => entry.key);

  return {
    tab: state.activeTab,
    overlayKey: currentOverlayKey(),
    windowY: window.scrollY,
    scrollables,
  };
}

function restoreScrollSnapshot(snapshot: ScrollSnapshot): void {
  if (snapshot.tab !== state.activeTab) return;

  const overlayKey = currentOverlayKey();
  const sameOverlay = snapshot.overlayKey && snapshot.overlayKey === overlayKey;
  const noOverlay = !snapshot.overlayKey && !overlayKey;
  const overlayClosed = snapshot.overlayKey && !overlayKey;

  if (sameOverlay) {
    for (const entry of snapshot.scrollables) {
      const selector = `[data-scroll-preserve="${escapeSelectorValue(entry.key)}"]`;
      const element = document.querySelector<HTMLElement>(selector);
      if (!element) continue;
      element.scrollTop = entry.top;
      element.scrollLeft = entry.left;
    }
  }

  if (sameOverlay || noOverlay || overlayClosed) {
    window.scrollTo({ top: snapshot.windowY, left: 0, behavior: "instant" as ScrollBehavior });
  }
}

function captureActiveField(): ActiveFieldSnapshot | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement)) {
    return null;
  }

  const dataField = active.dataset["field"];
  const tag = active.tagName.toLowerCase();
  const selector = dataField
    ? `${tag}[data-field="${escapeSelectorValue(dataField)}"]`
    : (active.id
      ? `#${escapeSelectorValue(active.id)}`
      : (active.getAttribute("name")
        ? `${tag}[name="${escapeSelectorValue(active.getAttribute("name") || "")}"]`
        : ""));

  if (!selector) return null;

  return {
    selector,
    value: "value" in active ? active.value : undefined,
    selectionStart: active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ? active.selectionStart : null,
    selectionEnd: active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ? active.selectionEnd : null,
    tab: state.activeTab,
  };
}

function restoreActiveField(snapshot: ActiveFieldSnapshot | null): void {
  if (!snapshot || snapshot.tab !== state.activeTab) return;

  const next = document.querySelector(snapshot.selector);
  if (!(next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement || next instanceof HTMLSelectElement)) {
    return;
  }

  if (snapshot.value !== undefined && next.value !== snapshot.value) {
    next.value = snapshot.value;
  }

  next.focus({ preventScroll: true });

  if (
    (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) &&
    snapshot.selectionStart !== null &&
    snapshot.selectionStart !== undefined &&
    snapshot.selectionEnd !== null &&
    snapshot.selectionEnd !== undefined
  ) {
    try {
      next.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
    } catch {
      // Some input types do not support selection ranges. Keeping focus is enough.
    }
  }
}

function resizeComposerTextArea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  const minHeight = Number.parseFloat(window.getComputedStyle(textarea).minHeight || "0") || 0;
  textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
}

function syncWritingSurfaces(): void {
  const composerTextarea = document.querySelector<HTMLTextAreaElement>("textarea[data-field='composer-text']");
  if (composerTextarea) resizeComposerTextArea(composerTextarea);
}

// ── Overlay history sync (web only) ────────────────────────────────────────
// Native uses installNativeBackButtonHandling()'s hardware back-button
// listener; on web there's no such hook, so without this the browser Back
// button either strands an open overlay over a different tab or exits the
// app entirely, and refreshing mid-overlay always discards it. We push a
// history entry the moment any overlay opens, and mirror the same
// reverse-depth-order priority the native handler uses to close the
// topmost one on Back (see popstate handler) or on programmatic close.
let overlayHistoryPushed = false;
let suppressHistoryPopOnClose = false;

function isOverlayOpen(): boolean {
  return !!(
    isCompanionUpdateOverlayOpen() ||
    state.confirmModal ||
    state.thinkingTimeline.selectedPatternId ||
    state.companionSession ||
    state.communityOverlay ||
    state.publicProfile ||
    state.threadModal
  );
}

function syncOverlayHistory(): void {
  if (Capacitor.isNativePlatform()) return;
  const overlayOpenNow = isOverlayOpen();
  if (overlayOpenNow && !overlayHistoryPushed) {
    overlayHistoryPushed = true;
    window.history.pushState({ lotOverlay: true }, "", window.location.href);
    return;
  }
  if (!overlayOpenNow && overlayHistoryPushed) {
    overlayHistoryPushed = false;
    if (suppressHistoryPopOnClose) {
      // We're here because popstate itself closed the overlay — the browser
      // already consumed that history entry, so popping again would skip
      // past the page the user actually meant to land on.
      suppressHistoryPopOnClose = false;
      return;
    }
    // The overlay was dismissed via an in-app action (X button, backdrop
    // click, etc), not Back — remove the now-stale entry we pushed so a
    // follow-up Back press doesn't need to be pressed twice.
    window.history.back();
  }
}

/// Keeps the direction-chip arrows honest: shown only when the row actually
/// overflows, and each one disappears at its own end. Runs after render, on
/// row scroll (capture phase — scroll doesn't bubble), and on resize.
function updateDirectionScrollState(): void {
  const block = document.querySelector<HTMLElement>(".discovery-directions-block");
  const row = block?.querySelector<HTMLElement>(".discovery-directions");
  if (!block || !row) return;
  const overflow = row.scrollWidth > row.clientWidth + 1;
  block.classList.toggle("has-overflow", overflow);
  block.classList.toggle("at-start", row.scrollLeft <= 1);
  block.classList.toggle("at-end", row.scrollLeft + row.clientWidth >= row.scrollWidth - 1);
}

document.addEventListener("scroll", (event) => {
  if ((event.target as HTMLElement)?.classList?.contains("discovery-directions")) {
    updateDirectionScrollState();
  }
}, true);

window.addEventListener("resize", () => updateDirectionScrollState());
// Chip widths settle after fonts load; the overflow answer can change then.
window.addEventListener("load", () => updateDirectionScrollState());
document.fonts?.ready?.then?.(() => updateDirectionScrollState());

// ── Typing gate ────────────────────────────────────────────────────────────
// The app re-renders wholesale, which rebuilds the exact input the user is
// typing into (caret jumps, extensions like Grammarly re-attach, resize
// handles reset). Background renders — Firestore listeners, timers, fetch
// completions — must therefore WAIT while a text field is active. Renders
// dispatched from a user event (click Send, Enter, input handlers) still go
// through immediately: `window.event` is defined only during event dispatch.
let deferredRenderPending = false;
let deferredFlushTimer: number | null = null;
let flushingDeferredRender = false;
let lastTypingInputAt = 0;

function activeTypingField(): HTMLElement | null {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return null;
  if (el instanceof HTMLTextAreaElement) return el;
  if (el instanceof HTMLInputElement) {
    const type = (el.type || "text").toLowerCase();
    return ["text", "search", "email", "password", "url", "tel", "number"]
      .includes(type) ? el : null;
  }
  return el.isContentEditable ? el : null;
}

function flushDeferredRenderIfIdle(): void {
  deferredFlushTimer = null;
  if (!deferredRenderPending) return;
  // Still mid-typing — keep waiting. 1.5s means a keystroke burst is
  // never interrupted, while an arriving Companion reply still lands
  // within a beat of the user pausing.
  if (activeTypingField() && Date.now() - lastTypingInputAt < 1500) {
    deferredFlushTimer = window.setTimeout(flushDeferredRenderIfIdle, 500);
    return;
  }
  deferredRenderPending = false;
  flushingDeferredRender = true;
  try {
    render();
  } finally {
    flushingDeferredRender = false;
  }
}

document.addEventListener("input", () => {
  lastTypingInputAt = Date.now();
}, true);

document.addEventListener("focusout", () => {
  if (!deferredRenderPending) return;
  // Let focus settle first; if it lands in another text field the flush
  // check re-defers on its own.
  window.setTimeout(flushDeferredRenderIfIdle, 60);
});

// The tab title follows the view, the way a website's does: history entries
// and browser tabs read as places ("Discovery · LOT"), and an open thought
// is titled by its own words — the same title the server gives /t/{id}.
const SITE_TITLE = "LOT — The Language of Thoughts Platform";
function syncDocumentTitle(): void {
  let title = SITE_TITLE;
  if (state.threadModal?.post?.text) {
    const text = String(state.threadModal.post.text).replace(/\s+/g, " ").trim();
    const excerpt = text.length > 72 ? `${text.slice(0, 71).trimEnd()}…` : text;
    title = `"${excerpt}" — LOT`;
  } else if (state.user || state.guestMode) {
    const label = TAB_LABELS[state.activeTab as keyof typeof TAB_LABELS];
    if (label) title = `${label} · LOT`;
  }
  if (document.title !== title) document.title = title;
}

export function render(): void {
  if (!flushingDeferredRender && activeTypingField() && !window.event) {
    deferredRenderPending = true;
    if (deferredFlushTimer === null) {
      deferredFlushTimer = window.setTimeout(flushDeferredRenderIfIdle, 1000);
    }
    return;
  }
  // Tells the boot watchdog (inline script in index.html) that the app
  // successfully executed far enough to render — see index.html for why.
  (window as unknown as { __LOT_APP_BOOTED__?: boolean }).__LOT_APP_BOOTED__ = true;
  syncOverlayHistory();
  const prevTab = _lastRenderedTab;
  const scrollSnapshot = captureScrollSnapshot();
  const activeField = captureActiveField();
  renderUI();
  syncDocumentTitle();
  syncWritingSurfaces();
  restoreScrollSnapshot(scrollSnapshot);
  restoreActiveField(activeField);
  syncWritingSurfaces();
  bindProgressiveFeed();
  requestAnimationFrame(() => {
    syncWritingSurfaces();
    restoreScrollSnapshot(scrollSnapshot);
    updateDirectionScrollState();
  });
  const nextTab = state.activeTab;

  // Tab-switch animation (only when the active tab actually changed)
  if (prevTab !== nextTab && !state.loadingAuth && state.profileResolved && state.profile?.username) {
    _lastRenderedTab = nextTab;
    const mainEl = document.getElementById("main-content");
    if (mainEl) {
      // Directional iOS-style push: content slides in from the right when
      // advancing through the tab order, from the left when going back —
      // mirroring a UINavigationController push/pop. Falls back to a forward
      // slide if either tab is missing from TAB_ORDER.
      const order = TAB_ORDER as readonly string[];
      const prevIdx = order.indexOf(prevTab);
      const nextIdx = order.indexOf(nextTab);
      const goingBack = prevIdx >= 0 && nextIdx >= 0 && nextIdx < prevIdx;
      mainEl.classList.remove("tab-entering", "tab-entering-back");
      // Force reflow so removing + re-adding the class triggers the animation
      void (mainEl as HTMLElement).offsetWidth;
      mainEl.classList.add(goingBack ? "tab-entering-back" : "tab-entering");
      mainEl.addEventListener("animationend", () => {
        mainEl.classList.remove("tab-entering", "tab-entering-back");
      }, { once: true });
    }
    // Bounce the freshly-selected bottom-nav icon (iOS tab-bar feel). Gated
    // here rather than via :active because the shell re-renders on every state
    // change — this fires the pop only on an actual tab switch.
    const activeNavItem = document.querySelector<HTMLElement>(".bottom-nav-item.active");
    if (activeNavItem) {
      activeNavItem.classList.add("nav-pop");
      activeNavItem.addEventListener("animationend", () => {
        activeNavItem.classList.remove("nav-pop");
      }, { once: true });
    }
  } else {
    _lastRenderedTab = nextTab;
  }

  // Body scroll lock — prevent background scroll when any overlay is open
  const hasOverlay =
    !!state.threadModal ||
    !!state.publicProfile ||
    !!state.thinkingTimeline.selectedPatternId ||
    !!state.companionSession ||
    !!state.thoughtMatchExcerptDraft ||
    !!state.selectedMatchConversationId ||
    !!state.selectedMatchExcerpt ||
    !!state.communityOverlay ||
    !!state.confirmModal;
  if (hasOverlay) {
    if (!document.body.classList.contains("modal-open")) {
      // Measure scrollbar width to prevent layout shift
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.setProperty("--scrollbar-w", `${scrollbarW}px`);
      document.body.classList.add("modal-open");
    }
  } else {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("--scrollbar-w");
  }
}

function bindProgressiveFeed(): void {
  feedLoadObserver?.disconnect();
  feedLoadObserver = null;

  const sentinel = document.querySelector<HTMLElement>("[data-feed-load-more]");
  if (!sentinel || state.activeTab !== "feed") return;

  feedLoadObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    feedLoadObserver?.disconnect();
    state.feedVisibleLimit += 15;
    render();
  }, { rootMargin: "320px 0px" });
  feedLoadObserver.observe(sentinel);
}

export function setBusy(key: string, busy: boolean): void {
  if (busy) state.busy.add(key);
  else state.busy.delete(key);
  render();
}

// Register dependency injection
registerServices(render, setBusy);

// Localhost-only inspection handle — never shipped behavior, just a window
// into live state for debugging without a devtools build.
if (typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
  (window as unknown as { __lotDebug?: unknown }).__lotDebug = { state };
}

// ── Tab helpers ───────────────────────────────────────────────────────────
function setActiveTab(tab: string, shouldPushHistory = false): void {
  const safeTab: TabName = (TAB_ORDER as readonly string[]).includes(tab) ? (tab as TabName) : "notes";
  state.activeTab = safeTab;
  if (shouldPushHistory) {
    const nextPath = pathForTab(safeTab);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({ tab: safeTab }, "", `${nextPath}${window.location.search}`);
    }
  }
}

function inlineSimilarNoteKey(noteId: string): string {
  return `note:${noteId}`;
}

function similarThoughtOpenKey(scope: string, postId: string, groupId = ""): string {
  return `${scope || "sharedPost"}:${groupId || ""}:${postId}`;
}

async function toggleInlineSimilarThoughts(target: HTMLElement): Promise<void> {
  const source = target.dataset["similarSource"] || "post";
  if (source !== "note") return;

  const noteId = target.dataset["noteId"] || target.dataset["sourceId"] || "";
  if (!noteId) return;
  const key = inlineSimilarNoteKey(noteId);
  if (state.expandedSimilarThoughtIds.has(key)) {
    state.expandedSimilarThoughtIds.delete(key);
    render();
    return;
  }

  state.expandedSimilarThoughtIds.add(key);
  render();

  const note = state.notes.find((entry) => entry.id === noteId);
  const canFetchFreshMatches = !!note
    && !note.isEncrypted
    && state.aiPrefs.notesAiEnabled
    && state.aiPrefs.notesAIDisclosureAccepted;
  if (!canFetchFreshMatches || state.inlineSimilarThoughtLoading.has(key)) return;

  state.inlineSimilarThoughtLoading.add(key);
  render();
  try {
    await ensureNoteSimilarThoughts(noteId);
  } finally {
    state.inlineSimilarThoughtLoading.delete(key);
    render();
  }
}

let pendingNativeUrl: string | null = null;

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionErrorLike = {
  error: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

let companionRecognition: SpeechRecognitionLike | null = null;
let companionVoiceActive = false;
let companionVoiceBase = "";
let companionVoiceCommitted = "";
let companionVoiceSessionId = "";
let composerRecognition: SpeechRecognitionLike | null = null;
let composerVoiceActive = false;
let composerVoiceBase = "";
let composerVoiceCommitted = "";

function speechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  const candidate = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return candidate.SpeechRecognition || candidate.webkitSpeechRecognition || null;
}

function companionVoiceText(interim = ""): string {
  return [companionVoiceBase, companionVoiceCommitted, interim]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
}

function updateCompanionVoiceDraft(interim = ""): void {
  const session = state.companionSession;
  if (!session || session.sessionId !== companionVoiceSessionId) return;
  const text = companionVoiceText(interim);
  session.input = text;
  const textarea = document.querySelector<HTMLTextAreaElement>(
    "textarea[data-field='companion-input']",
  );
  if (textarea) {
    textarea.value = text;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`;
  }
  const send = document.querySelector<HTMLButtonElement>("[data-action='send-companion-message']");
  if (send) send.disabled = !text.trim() || session.isSending;
}

function stopCompanionVoiceInput(renderAfter = true): void {
  companionVoiceActive = false;
  if (Capacitor.isNativePlatform()) {
    void NativeSpeechRecognition.forceStop({ timeout: 1200 }).catch(() => {
      return NativeSpeechRecognition.stop().catch(() => {});
    });
    void NativeSpeechRecognition.removeAllListeners().catch(() => {});
  }
  try {
    companionRecognition?.stop();
  } catch {
    companionRecognition?.abort();
  }
  companionRecognition = null;
  if (state.companionSession) state.companionSession.isListening = false;
  if (renderAfter) render();
}

async function startCompanionVoiceInput(): Promise<void> {
  const session = state.companionSession;
  if (!session) return;

  if (Capacitor.isNativePlatform()) {
    stopCompanionVoiceInput(false);
    companionVoiceActive = true;
    companionVoiceBase = session.input.trim();
    companionVoiceCommitted = "";
    companionVoiceSessionId = session.sessionId;
    session.isListening = true;
    render();
    try {
      const availability = await NativeSpeechRecognition.available();
      if (!availability.available) throw new Error("native_speech_unavailable");
      let permission = await NativeSpeechRecognition.checkPermissions();
      if (permission.speechRecognition !== "granted") {
        permission = await NativeSpeechRecognition.requestPermissions();
      }
      if (permission.speechRecognition !== "granted") {
        throw new Error("native_speech_permission");
      }
      await NativeSpeechRecognition.removeAllListeners();
      await NativeSpeechRecognition.addListener("partialResults", (event) => {
        if (!companionVoiceActive || state.companionSession?.sessionId !== companionVoiceSessionId) return;
        const transcript = String(
          event.accumulatedText ||
          event.accumulated ||
          event.matches?.[0] ||
          "",
        ).trim();
        companionVoiceCommitted = transcript;
        updateCompanionVoiceDraft();
      });
      await NativeSpeechRecognition.addListener("error", () => {
        if (!companionVoiceActive) return;
        stopCompanionVoiceInput(false);
        showToast("Voice input paused. Tap Mic to continue.");
        render();
      });
      await NativeSpeechRecognition.start({
        language: navigator.language || "en-US",
        maxResults: 1,
        popup: false,
        partialResults: true,
        addPunctuation: true,
        allowForSilence: 1800,
        continuousPTT: true,
      });
      return;
    } catch {
      stopCompanionVoiceInput(false);
      showToast("Microphone permission is needed for voice input.");
      render();
      return;
    }
  }

  const Recognition = speechRecognitionConstructor();
  if (!Recognition) {
    showToast("Voice input is not available in this browser.");
    return;
  }
  stopCompanionVoiceInput(false);
  companionVoiceActive = true;
  companionVoiceBase = session.input.trim();
  companionVoiceCommitted = "";
  companionVoiceSessionId = session.sessionId;
  session.isListening = true;

  const startSegment = () => {
    if (!companionVoiceActive || state.companionSession?.sessionId !== companionVoiceSessionId) return;
    const recognition = new Recognition();
    companionRecognition = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = navigator.language || "en-US";
    recognition.onresult = (event) => {
      if (!companionVoiceActive || recognition !== companionRecognition) return;
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = String(result?.[0]?.transcript || "").trim();
        if (!transcript) continue;
        if (result.isFinal) {
          companionVoiceCommitted = [companionVoiceCommitted, transcript]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(" ");
        } else {
          interim = [interim, transcript].filter(Boolean).join(" ");
        }
      }
      updateCompanionVoiceDraft(interim);
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        companionVoiceActive = false;
        if (state.companionSession) state.companionSession.isListening = false;
        showToast("Microphone permission is needed for voice input.");
        render();
      }
    };
    recognition.onend = () => {
      if (!companionVoiceActive || recognition !== companionRecognition) return;
      updateCompanionVoiceDraft();
      window.setTimeout(startSegment, 120);
    };
    try {
      recognition.start();
    } catch {
      companionVoiceActive = false;
      if (state.companionSession) state.companionSession.isListening = false;
      showToast("Voice input could not start.");
      render();
    }
  };

  render();
  window.setTimeout(startSegment, 0);
}

function toggleCompanionVoiceInput(): void {
  if (companionVoiceActive) stopCompanionVoiceInput();
  else void startCompanionVoiceInput();
}

function updateComposerVoiceDraft(interim = ""): void {
  const text = [composerVoiceBase, composerVoiceCommitted, interim]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  state.composer.text = text;
  const textarea = document.querySelector<HTMLTextAreaElement>("textarea[data-field='composer-text']");
  if (textarea) {
    textarea.value = text;
    resizeComposerTextArea(textarea);
  }
}

function stopComposerVoiceInput(renderAfter = true): void {
  composerVoiceActive = false;
  state.composer.isListening = false;
  if (Capacitor.isNativePlatform()) {
    void NativeSpeechRecognition.forceStop({ timeout: 1200 }).catch(() => {
      return NativeSpeechRecognition.stop().catch(() => {});
    });
    void NativeSpeechRecognition.removeAllListeners().catch(() => {});
  }
  try {
    composerRecognition?.stop();
  } catch {
    composerRecognition?.abort();
  }
  composerRecognition = null;
  updateComposerVoiceDraft();
  if (renderAfter) render();
}

async function startComposerVoiceInput(): Promise<void> {
  composerVoiceBase = state.composer.text.trim();
  composerVoiceCommitted = "";
  composerVoiceActive = true;
  state.composer.isListening = true;
  render();

  if (Capacitor.isNativePlatform()) {
    try {
      const availability = await NativeSpeechRecognition.available();
      if (!availability.available) throw new Error("native_speech_unavailable");
      let permission = await NativeSpeechRecognition.checkPermissions();
      if (permission.speechRecognition !== "granted") {
        permission = await NativeSpeechRecognition.requestPermissions();
      }
      if (permission.speechRecognition !== "granted") throw new Error("native_speech_permission");
      await NativeSpeechRecognition.removeAllListeners();
      await NativeSpeechRecognition.addListener("partialResults", (event) => {
        if (!composerVoiceActive) return;
        composerVoiceCommitted = String(
          event.accumulatedText || event.accumulated || event.matches?.[0] || "",
        ).trim();
        updateComposerVoiceDraft();
      });
      await NativeSpeechRecognition.addListener("error", () => {
        if (!composerVoiceActive) return;
        stopComposerVoiceInput(false);
        showToast("Voice input paused. Tap Mic to continue.");
        render();
      });
      await NativeSpeechRecognition.start({
        language: navigator.language || "en-US",
        maxResults: 1,
        popup: false,
        partialResults: true,
        addPunctuation: true,
        allowForSilence: 1800,
        continuousPTT: true,
      });
      return;
    } catch {
      stopComposerVoiceInput(false);
      showToast("Microphone permission is needed for voice input.");
      render();
      return;
    }
  }

  const Recognition = speechRecognitionConstructor();
  if (!Recognition) {
    composerVoiceActive = false;
    state.composer.isListening = false;
    showToast("Voice input is not available in this browser.");
    render();
    return;
  }
  const startSegment = () => {
    if (!composerVoiceActive) return;
    const recognition = new Recognition();
    composerRecognition = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = navigator.language || "en-US";
    recognition.onresult = (event) => {
      if (!composerVoiceActive || recognition !== composerRecognition) return;
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = String(result?.[0]?.transcript || "").trim();
        if (!transcript) continue;
        if (result.isFinal) {
          composerVoiceCommitted = [composerVoiceCommitted, transcript]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(" ");
        } else {
          interim = [interim, transcript].filter(Boolean).join(" ");
        }
      }
      updateComposerVoiceDraft(interim);
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        composerVoiceActive = false;
        state.composer.isListening = false;
        showToast("Microphone permission is needed for voice input.");
        render();
      }
    };
    recognition.onend = () => {
      if (!composerVoiceActive || recognition !== composerRecognition) return;
      updateComposerVoiceDraft();
      window.setTimeout(startSegment, 120);
    };
    try {
      recognition.start();
    } catch {
      composerVoiceActive = false;
      state.composer.isListening = false;
      showToast("Voice input could not start.");
      render();
    }
  };
  window.setTimeout(startSegment, 0);
}

function toggleComposerVoiceInput(): void {
  if (composerVoiceActive) stopComposerVoiceInput();
  else void startComposerVoiceInput();
}

async function handleIncomingUrl(rawUrl: string): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return;
  }

  const isLotWebUrl = parsed.protocol === "https:" && parsed.hostname === "lotapp.ai";
  const isLotScheme = parsed.protocol === "lot:";
  if (!isLotWebUrl && !isLotScheme) return;

  let postId = "";
  let inviteCode = "";
  let appPath = parsed.pathname || "/app";

  if (isLotScheme) {
    if (parsed.hostname === "post") postId = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
    if (parsed.hostname === "community-code") inviteCode = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
    appPath = "/app";
  } else {
    const parts = parsed.pathname.split("/").filter(Boolean);
    if ((parts[0] === "t" || parts[0] === "post" || parts[0] === "p") && parts[1]) postId = decodeURIComponent(parts[1]);
    if (parts[0] === "join" && parts[1]) inviteCode = decodeURIComponent(parts[1]);
  }

  if (postId) {
    const nextPath = `/app?post=${encodeURIComponent(postId)}`;
    window.history.pushState({ tab: "feed" }, "", nextPath);
    setActiveTab("feed", false);
    render();
    await openSharedThread(postId);
    return;
  }

  if (inviteCode) {
    // Communities were retired in 1.8 (matching iOS): an old invite link
    // lands on Discovery instead of opening a dead surface.
    window.history.pushState({ tab: "feed" }, "", "/feed");
    setActiveTab("feed", false);
    render();
    showToast("Communities have been retired. Welcome to Discovery.");
    return;
  }

  if (appPath.startsWith("/app")) {
    const nextTab = tabFromPath(appPath);
    window.history.pushState({ tab: nextTab }, "", `${appPath}${parsed.search}`);
    setActiveTab(nextTab, false);
    render();
  }
}

async function syncNativeChrome(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    // Android light-mode WebViews can wash the system bar into LOT's pale
    // neural background. Keep native chrome dark and explicit so status icons,
    // the top app bar, and bottom navigation read as intentional app surfaces.
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: "#07111D" });
    await StatusBar.setStyle({ style: Style.Light });
  } catch (error) {
    console.warn("Native status bar setup failed", error);
  }
}

// ── Haptic tap feedback ──
// Mirrors LOTTheme.haptic(.light) on iOS — every primary interaction gets a
// soft impact pulse on press-down (not press-up), matching iOS button feel.
// Coalesced to ≤1 pulse per 60ms so rapid taps don't queue a buzzy chain.
let lastHapticAt = 0;
function tapHaptic(style: ImpactStyle = ImpactStyle.Light): void {
  if (!Capacitor.isNativePlatform()) return;
  const now = Date.now();
  if (now - lastHapticAt < 60) return;
  lastHapticAt = now;
  void Haptics.impact({ style }).catch(() => {});
}

function installHapticFeedback(): void {
  if (!Capacitor.isNativePlatform()) return;
  // pointerdown beats click: the haptic fires at the moment the finger
  // contacts the screen, not after the event bubbles up.
  document.addEventListener("pointerdown", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const interactive = target.closest(
      "[data-action], button, a, [role=\"button\"], .post-card, .community-card, .friend-card, .note-card, .bottom-nav-item",
    );
    if (!interactive) return;
    // Stronger pulse for primary CTAs and resonance taps — softer for cards
    // and nav items so the feed doesn't buzz on every scroll-tap.
    const heavy = interactive.matches(".btn-primary, .felt-btn, .resonance-btn, [data-haptic=\"medium\"]");
    tapHaptic(heavy ? ImpactStyle.Medium : ImpactStyle.Light);
  }, { passive: true, capture: true });
}

/** Force a repaint after the app / browser window comes back from the background.
 *
 * Chrome (and Android WebView) can release GPU compositor textures when the
 * window is minimised.  On restore, certain layers — especially text inside
 * stacking contexts created by backdrop-filter, will-change, or fixed-position
 * elements — stay blank even though the DOM is intact.  Toggling opacity by a
 * sub-visible amount invalidates every compositor layer and triggers a full
 * repaint without any visible flash.  Called on ALL platforms, not just Android,
 * because the same bug reproduces in desktop Chrome. */
function installForegroundRepaintFix(): void {
  const repaint = () => {
    const target = document.querySelector<HTMLElement>(".main-content") ?? document.body;
    target.style.opacity = "0.9999";
    requestAnimationFrame(() => { target.style.opacity = ""; });
  };

  // Page Visibility API — covers minimize/restore on desktop Chrome and
  // Android Chrome, plus tab-switch in any browser.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) repaint();
  });

  // Capacitor-specific surface for the native Android shell.
  if (Capacitor.getPlatform() === "android") {
    void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) repaint();
    });
  }
}

function installNativeAppChrome(): void {
  if (!Capacitor.isNativePlatform()) return;

  document.documentElement.classList.add("capacitor-native", `capacitor-${Capacitor.getPlatform()}`);
  document.body.classList.add("capacitor-native", `capacitor-${Capacitor.getPlatform()}`);

  void syncNativeChrome();
  try {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      void syncNativeChrome();
    });
  } catch {
    // Older WebViews may not support addEventListener on MediaQueryList.
  }

  if (Capacitor.getPlatform() === "ios") {
    void Keyboard.setResizeMode({ mode: KeyboardResize.Body }).catch(() => {});
  }
  installHapticFeedback();
  void SplashScreen.hide({ fadeOutDuration: 220 }).catch(() => {});
}

let nativePushListenersInstalled = false;
let nativePushRegistering = false;

function notificationDataValue(data: unknown, keys: string[]): string {
  if (!data || typeof data !== "object") return "";
  const record = data as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
}

function routeNativeNotificationData(data: unknown): void {
  const directUrl = notificationDataValue(data, ["deepLink", "deeplink", "url", "link"]);
  if (directUrl) {
    void handleIncomingUrl(directUrl);
    return;
  }

  const postId = notificationDataValue(data, ["postId", "postID", "threadId", "sharedPostId"]);
  if (postId) {
    void handleIncomingUrl(`https://lotapp.ai/t/${encodeURIComponent(postId)}`);
    return;
  }

  const inviteCode = notificationDataValue(data, ["inviteCode", "joinCode", "communityCode"]);
  if (inviteCode) {
    void handleIncomingUrl(`https://lotapp.ai/join/${encodeURIComponent(inviteCode)}`);
    return;
  }

  const tab = notificationDataValue(data, ["tab", "targetTab"]);
  if (tab) {
    setActiveTab(tab, true);
    render();
  }
}

function focusReplyComposer(): void {
  requestAnimationFrame(() => {
    const modal = document.querySelector<HTMLElement>(".modal");
    const textarea = modal?.querySelector<HTMLTextAreaElement>("textarea[data-field='reply-text']");
    if (!modal || !textarea || textarea.disabled) return;
    textarea.scrollIntoView({ block: "center", behavior: "smooth" });
    textarea.focus({ preventScroll: true });
  });
}

function focusComposerText(): void {
  requestAnimationFrame(() => {
    const textarea = document.querySelector<HTMLTextAreaElement>("textarea[data-field='composer-text']");
    if (!textarea || textarea.disabled) return;
    textarea.scrollIntoView({ block: "center", behavior: "smooth" });
    textarea.focus({ preventScroll: true });
  });
}

function reviewPublicThoughtText(text: string): { text: string; changeCount: number } {
  let reviewed = String(text || "");
  let changeCount = 0;

  const replace = (pattern: RegExp, replacement: string | ((substring: string, ...args: string[]) => string)): void => {
    const next = reviewed.replace(pattern, replacement as string);
    if (next !== reviewed) {
      changeCount += 1;
      reviewed = next;
    }
  };

  replace(/\b(?:api[_\s-]?key|token|password|passcode|secret|private[_\s-]?key)\s*[:=]\s*\S+/gi, "[secret removed]");
  replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[email removed]");
  replace(/\bhttps?:\/\/\S+|\bwww\.\S+/gi, "[link removed]");
  replace(/\b\d{3}-\d{2}-\d{4}\b/g, "[ID removed]");
  replace(/\b(?:\d[ -]*?){13,16}\b/g, "[number removed]");
  replace(/\+?\b(?:\d[\s().-]?){8,}\d\b/g, "[phone removed]");
  replace(
    /\b\d{1,6}\s+[A-Za-z0-9.'-]+(?:\s+[A-Za-z0-9.'-]+){0,4}\s+(?:Street|St\.?|Avenue|Ave\.?|Road|Rd\.?|Drive|Dr\.?|Lane|Ln\.?|Boulevard|Blvd\.?|Court|Ct\.?|Way|Place|Pl\.)\b/gi,
    "[address removed]",
  );
  replace(/(^|[\s(])@[A-Za-z0-9_.]{2,30}\b/g, (_match, prefix = "") => `${prefix}[handle removed]`);

  reviewed = reviewed
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();

  return { text: reviewed, changeCount };
}

function installNativePushListeners(): void {
  if (!Capacitor.isNativePlatform() || nativePushListenersInstalled) return;
  nativePushListenersInstalled = true;

  void PushNotifications.addListener("registration", (token) => {
    void saveNativePushToken(token.value, Capacitor.getPlatform()).catch((error) => {
      console.warn("Could not save native push token", error);
    });
  });

  void PushNotifications.addListener("registrationError", (error) => {
    console.warn("Native push registration failed", error);
  });

  void PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    routeNativeNotificationData(event.notification?.data);
  });
}

async function registerNativePushIfPossible(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !state.user || nativePushRegistering) return;
  nativePushRegistering = true;
  try {
    installNativePushListeners();
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== "granted") return;
    await PushNotifications.register();
  } catch (error) {
    console.warn("Native push setup failed", error);
  } finally {
    nativePushRegistering = false;
  }
}

function installNativeDeepLinkHandling(): void {
  if (!Capacitor.isNativePlatform()) return;
  void CapacitorApp.getLaunchUrl().then((launch) => {
    const url = launch?.url || "";
    if (!url) return;
    if (state.loadingAuth) {
      pendingNativeUrl = url;
      return;
    }
    void handleIncomingUrl(url);
  }).catch(() => {});
  void CapacitorApp.addListener("appUrlOpen", ({ url }) => {
    if (state.loadingAuth) {
      pendingNativeUrl = url;
      return;
    }
    void handleIncomingUrl(url);
  });
}

function installNativeBackButtonHandling(): void {
  if (!Capacitor.isNativePlatform()) return;
  void CapacitorApp.addListener("backButton", () => {
    // Dismiss overlays in reverse order of depth
    if (closeUpdateOverlay()) return;
    if (state.confirmModal) {
      const cancel = state.confirmModal.onCancel;
      state.confirmModal = null;
      render();
      cancel?.();
      return;
    }
    if (state.thinkingTimeline.selectedPatternId) {
      state.thinkingTimeline.selectedPatternId = null;
      render();
      return;
    }
    if (state.companionSession) {
      stopCompanionVoiceInput(false);
      void finishCompanionSession();
      return;
    }
    if (state.communityOverlay) {
      state.communityOverlay = null;
      render();
      return;
    }
    if (state.publicProfile) {
      closePublicProfile();
      return;
    }
    if (state.threadModal) {
      void closeThreadModal();
      return;
    }
    // Navigate up within communities sub-pages
    if (state.communitySubPage !== "browse") {
      state.communitySubPage = "browse";
      render();
      return;
    }
    // Go back to feed from any non-feed tab
    if (state.activeTab !== "feed") {
      setActiveTab("feed", true);
      render();
      return;
    }
    // Already on root with no overlays — exit the app
    void CapacitorApp.exitApp();
  });
}

// ── Lookup helpers ────────────────────────────────────────────────────────
function lookupSharedPost(postId: string) {
  return state.feedPosts.find((p) => p.id === postId);
}

function lookupCommunityPost(postId: string) {
  return state.selectedCommunityPosts.find((p) => p.id === postId);
}

// ── Share/invite helpers ──────────────────────────────────────────────────
function handleAskFriend(post: { id: string; text: string }) {
  const url = `https://lotapp.ai/t/${encodeURIComponent(post.id)}`;
  const message = `I posted this thought on LOT. Curious what you think.\n\n${url}`;
  if (navigator.share) {
    navigator.share({ text: message }).catch(() => {});
    return;
  }
  navigator.clipboard.writeText(message).then(() => {
    showToast("Invite copied.");
  });
}

function wrapCanvasText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
}

async function shareResonanceMatch(): Promise<void> {
  const resonance = state.composerResonance;
  const match = resonance?.thoughts[0];
  if (!resonance || !match) return;
  const reason = match.matchReason || "A similar line of thinking";
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = "#fbfaf7";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#171514";
  ctx.font = "800 54px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText("LOT", 76, 110);
  ctx.font = "800 44px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText("Two thoughts found each other", 76, 210);

  const drawBlock = (label: string, text: string, y: number) => {
    ctx.fillStyle = "#68615b";
    ctx.font = "800 24px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.fillText(label.toUpperCase(), 76, y);
    ctx.fillStyle = "#171514";
    ctx.font = "500 40px -apple-system, BlinkMacSystemFont, sans-serif";
    wrapCanvasText(ctx, `"${text}"`, 920).slice(0, 6).forEach((line, index) => {
      ctx.fillText(line, 76, y + 62 + index * 54);
    });
  };

  drawBlock("You wrote", resonance.sourceText, 310);
  drawBlock("Matched thought", match.textPreview, 720);
  ctx.fillStyle = "#2f6f5e";
  ctx.font = "800 30px -apple-system, BlinkMacSystemFont, sans-serif";
  wrapCanvasText(ctx, reason, 920).slice(0, 2).forEach((line, index) => {
    ctx.fillText(line, 76, 1130 + index * 42);
  });
  ctx.fillStyle = "#68615b";
  ctx.font = "700 24px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText("Language of Thoughts", 76, 1260);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (blob && "canShare" in navigator && "share" in navigator) {
    const file = new File([blob], "lot-resonance-match.png", { type: "image/png" });
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: "LOT resonance match" });
      return;
    }
  }
  const text = `Two thoughts found each other on LOT.\n\n${reason}`;
  if (navigator.share) {
    await navigator.share({ title: "LOT resonance match", text }).catch(() => {});
    return;
  }
  await navigator.clipboard.writeText(text);
  showToast("Match copied.");
}

// ── In-app confirmation dialog ────────────────────────────────────────────
function askConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    state.confirmModal = {
      message,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    };
    render();
  });
}

// ── Auth error helpers ────────────────────────────────────────────────────
function currentAuthHandlerUrl(): string {
  const authDomain = (auth as unknown as { config?: { authDomain?: string } }).config?.authDomain || "language-of-thoughts.firebaseapp.com";
  return `https://${authDomain}/__/auth/handler`;
}

function shouldPreferRedirect(providerName: string): boolean {
  if (providerName !== "Apple") return false;
  return /iphone|ipad|ipod|android/i.test(navigator.userAgent);
}

function shouldFallbackToRedirect(error: unknown): boolean {
  const code = String((error as Record<string, unknown>)?.["code"] || "");
  return (
    code === "auth/popup-blocked" ||
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request" ||
    code === "auth/operation-not-supported-in-this-environment"
  );
}

function logAuthDiagnostics(error: unknown, providerName: string): void {
  const code = String((error as Record<string, unknown>)?.["code"] || "unknown");
  const authDomain = (auth as unknown as { config?: { authDomain?: string } }).config?.authDomain || "language-of-thoughts.firebaseapp.com";
  console.group(`${providerName} auth diagnostics`);
  console.log("Error code:", code);
  console.log("Error message:", (error as Error)?.message || "(none)");
  console.log("Current origin:", window.location.origin);
  console.log("Firebase authDomain:", authDomain);
  console.log("Expected Apple Return URL:", currentAuthHandlerUrl());
  console.groupEnd();
}

function authFailureMessage(error: unknown, providerName: string): string {
  const code = String((error as Record<string, unknown>)?.["code"] || "");
  const message = String((error as Error)?.message || "").toLowerCase();
  if (code === "auth/unauthorized-domain") {
    return `${providerName} sign-in is unavailable here right now. Try signing in with email instead.`;
  }
  if (code === "auth/operation-not-allowed") {
    return `${providerName} sign-in is unavailable right now. Try signing in with email instead.`;
  }
  if (code === "auth/popup-closed-by-user") {
    return `${providerName} sign-in was closed before completing.`;
  }
  if (code === "auth/popup-blocked") {
    return `${providerName} popup was blocked by your browser.`;
  }
  if (
    providerName === "Apple" &&
    (
      code === "auth/invalid-credential" ||
      code === "auth/invalid-app-credential" ||
      code === "auth/invalid-oauth-client-id" ||
      message.includes("invalid_request") ||
      message.includes("invalid web redirect url")
    )
  ) {
    return "Apple sign-in is unavailable right now. Try Google or email instead.";
  }
  if (providerName === "Apple") {
    return "Apple sign-in could not finish. Please try again, or use Google or email.";
  }
  return `${providerName} sign-in failed.`;
}

// ── handleAction ──────────────────────────────────────────────────────────
async function handleAction(action: string, target: Element): Promise<void> {
  if (action.startsWith("update-")) {
    const text = await handleUpdateAction(action, target);
    if (text) {
      await openCompanionSession(undefined, "What would you like to reflect on about your contribution?");
      if (state.companionSession) state.companionSession.input = text;
      render();
    }
    return;
  }
  // Your portrait and portrait matching own their own actions; the two
  // that destroy data stay here so they go through the shared confirm.
  if (action === "portrait-erase") {
    if (await askConfirm("Erase your portrait and everything behind it? This cannot be undone. Your thoughts stay exactly as they are.")) await erasePortrait();
    return;
  }
  if (action === "portrait-erase-claim") {
    const claimId = (target as HTMLElement).dataset["claimId"] || "";
    if (claimId && await askConfirm("Delete this from what LOT noticed? This cannot be undone.")) await performPortraitAction(claimId, "delete");
    return;
  }
  if (action === "portrait-match-consent-toggle") {
    await setPortraitMatchConsent((target as HTMLInputElement).checked);
    return;
  }
  if (action.startsWith("portrait-match-")) {
    if (await handlePortraitMatchingAction(action, target)) return;
  }
  if (action.startsWith("portrait-")) {
    if (await handlePortraitAction(action, target)) return;
  }
  switch (action) {
    case "auth-google": {
      await signInWithGoogle();
      break;
    }
    case "auth-apple": {
      await signInWithApple();
      break;
    }
    case "toggle-auth-mode": {
      state.authMode = state.authMode === "signin" ? "signup" : "signin";
      render();
      break;
    }
    case "auth-reset-password": {
      const email = getFieldValue("#email-auth-form input[name='email']", "").trim();
      await resetEmailPassword(email);
      break;
    }
    case "set-tab": {
      const tab = (target as HTMLElement).dataset["tab"];
      if (!tab) return;
      // Gate auth-required tabs for guest users
      if (state.guestMode && !state.user && tab !== "feed") {
        state.guestMode = false;
        render();
        break;
      }
      setActiveTab(tab, true);
      // Each Companion visit starts at the lead moment, like iOS.
      if (tab === "companion") state.companionMomentPage = 0;
      if (tab === "saved") void loadSavedPostDetails();
      if (tab === "portrait" && state.user) {
        state.portrait.mode = "home";
        void (async () => {
          await Promise.all([refreshPortraitAvailability(), refreshPortraitConsent()]);
          if (state.portrait.available && state.portrait.enabled) await loadPortrait();
          else render();
        })();
      }
      if (tab === "write" && state.user) {
        if ((target as HTMLElement).dataset["publicCompose"] === "true") {
          state.composer.visibility = "public";
          state.composer.matchConsent = true;
        } else if ((target as HTMLElement).dataset["privateCompose"] === "true") {
          state.composer.visibility = "private";
          state.composer.attribution = "anonymous";
          state.composerResonance = null;
        }
      }
      render();
      if (tab === "write" && state.user) {
        focusComposerText();
      }
      if (tab === "notes" && state.user) {
        void refreshVisibleNoteInsights(24);
        void fetchThinkingTimeline();
      }
      if (tab === "matches" && state.user) {
        void startThoughtMatching();
        void startPortraitMatching();
        if (state.portrait.claims.length === 0 && state.portrait.enabled) void loadPortrait();
      }
      if (tab === "companion" && state.user) {
        void loadCompanionContext();
        void fetchThinkingTimeline();
      }
      if (tab === "you" && state.user) {
        void fetchActivity();
      }
      break;
    }
    case "set-feed-filter": {
      state.feedFilter = ((target as HTMLElement).dataset["filter"] as "all" | "friends") || "all";
      state.feedFilterUserSet = true;   // user made an explicit choice; disable auto-default
      state.feedVisibleLimit = 15;
      render();
      break;
    }
    case "set-discovery-direction": {
      const direction = ((target as HTMLElement).dataset["direction"] || "").trim();
      state.selectedDiscoveryDirection = direction || null;
      state.discoveryShelfPostIds = null;
      state.feedVisibleLimit = 15;
      // A tag on a card can be tapped from anywhere: land on Discovery.
      if (state.activeTab !== "feed") setActiveTab("feed", true);
      render();
      void loadDiscoveryShelf(direction || null);
      break;
    }
    case "close-shared-notice": {
      state.sharedThoughtNotice = null;
      render();
      break;
    }
    case "refresh-feed": {
      console.debug("[LOT feed] refresh requested", { reason: "manual_refresh" });
      // Explicit refresh is the only re-rank boundary: drop the frozen
      // display order so the next render adopts the fresh server ranking.
      state.feedDisplayOrder = [];
      state.feedPendingNewIds = [];
      setBusy("load-page", true);
      await fetchFeed();
      state.feedVisibleLimit = 15;
      setBusy("load-page", false);
      break;
    }
    case "reveal-new-thoughts": {
      const list = document.querySelector(".discovery-post-list");
      const shouldScrollTop = !!list && list.getBoundingClientRect().top < 0;
      state.feedDisplayOrder = [...state.feedPendingNewIds, ...state.feedDisplayOrder];
      state.feedRevealAnimateIds = new Set(state.feedPendingNewIds);
      state.feedPendingNewIds = [];
      if (shouldScrollTop) window.scrollTo({ top: 0, behavior: "smooth" });
      render();
      // Cleared AFTER render so the fade-up animation applies exactly once.
      state.feedRevealAnimateIds = new Set();
      if (shouldScrollTop) {
        // render() restores the pre-render scroll position (including in a
        // follow-up rAF), which cancels the smooth scroll above — re-assert
        // it in a later rAF so the reader lands on the revealed thoughts.
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      }
      break;
    }
    case "load-more-feed": {
      state.feedVisibleLimit += 15;
      render();
      break;
    }
    case "use-prompt": {
      const prompt = (target as HTMLElement).dataset["prompt"] || "";
      state.composer.text = "";
      state.composer.placeholder = prompt || state.dailyPrompt.prompt;
      state.composerResonance = null;
      setActiveTab("write", true);
      render();
      focusComposerText();
      break;
    }
    case "use-note-prompt": {
      const prompt = (target as HTMLElement).dataset["prompt"] || "What keeps returning for you lately?";
      state.noteDraft.text = "";
      render();
      setTimeout(() => {
        const textarea = document.querySelector<HTMLTextAreaElement>("textarea[data-field='note-text']");
        if (textarea) {
          textarea.placeholder = prompt;
          textarea.focus();
        }
      }, 0);
      break;
    }
    case "create-post": {
      if (state.guestMode && !state.user) { state.guestMode = false; render(); break; }
      if (composerVoiceActive) stopComposerVoiceInput(false);
      // Public thoughts always pass through the safe-review step first (iOS
      // parity: "You review a safe version first.").
      const composerText = state.composer.text.trim();
      if (state.composer.visibility === "public" && composerText && !state.composer.safeReview) {
        const reviewed = reviewPublicThoughtText(composerText);
        state.composer.safeReview = {
          safeText: reviewed.text,
          originalText: composerText,
          changeCount: reviewed.changeCount,
          showOriginal: false,
        };
        render();
        break;
      }
      await createSharedPost();
      break;
    }
    case "cancel-share-review": {
      state.composer.safeReview = null;
      render();
      focusComposerText();
      break;
    }
    case "confirm-share-post": {
      const review = state.composer.safeReview;
      if (!review || !review.safeText.trim()) break;
      state.composer.text = review.safeText.trim();
      state.composer.safeReview = null;
      await createSharedPost();
      break;
    }
    case "review-public-thought": {
      const currentText = state.composer.text.trim();
      if (!currentText) {
        showToast("Write something first.");
        break;
      }
      const reviewed = reviewPublicThoughtText(currentText);
      if (reviewed.text !== currentText) {
        state.composer.text = reviewed.text;
        state.composer.placeholder = "";
        state.composerResonance = null;
        render();
        focusComposerText();
        showToast("Removed obvious sensitive details. Read once before publishing.");
      } else {
        showToast("No obvious sensitive details found. Still review before publishing.");
      }
      break;
    }
    case "toggle-composer-voice": {
      toggleComposerVoiceInput();
      break;
    }
    case "approve-match-excerpt": {
      await approveThoughtMatchExcerpt();
      break;
    }
    case "dismiss-match-excerpt": {
      dismissThoughtMatchExcerpt();
      break;
    }
    case "respond-match": {
      const proposalId = (target as HTMLElement).dataset["proposalId"];
      if (!proposalId) return;
      await respondToThoughtMatch(
        proposalId,
        (target as HTMLElement).dataset["accept"] === "true",
      );
      break;
    }
    case "open-match-conversation": {
      const conversationId = (target as HTMLElement).dataset["conversationId"];
      if (!conversationId) return;
      openThoughtMatchConversation(conversationId);
      break;
    }
    case "close-match-conversation": {
      closeThoughtMatchConversation();
      break;
    }
    case "send-match-message": {
      await sendThoughtMatchMessage();
      break;
    }
    case "leave-match-conversation": {
      const block = (target as HTMLElement).dataset["block"] === "true";
      if (await askConfirm(block
        ? "Block this person? The messages will disappear immediately."
        : "Leave this conversation? It closes for both people.")) {
        await leaveThoughtMatchConversation(block);
      }
      break;
    }
    case "report-match-conversation": {
      if (await askConfirm("Report this anonymous conversation?")) {
        await reportThoughtMatchConversation();
      }
      break;
    }
    case "open-match-excerpt": {
      state.selectedMatchExcerpt = {
        title: (target as HTMLElement).dataset["title"] || "Thought",
        text: (target as HTMLElement).dataset["text"] || "",
      };
      render();
      break;
    }
    case "close-match-excerpt": {
      state.selectedMatchExcerpt = null;
      render();
      break;
    }
    case "delete-post": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      if (await askConfirm("Delete this post?")) {
        await deleteSharedPost(postId);
      }
      break;
    }
    case "hide-post": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      await hideSharedPost(postId);
      break;
    }
    case "report-post": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      if (await askConfirm("Report and hide this post?")) {
        await reportSharedPost(postId);
      }
      break;
    }
    case "block-post-author": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      if (await askConfirm("Block this user and hide their posts?")) {
        await blockSharedPostAuthor(postId);
      }
      break;
    }
    case "open-thread": {
      const scope = (target as HTMLElement).dataset["scope"];
      const postId = (target as HTMLElement).dataset["postId"];
      const shouldFocusReply = (target as HTMLElement).dataset["focusReply"] === "true";
      if (!scope || !postId) return;
      if (state.publicProfile) {
        state.publicProfile = null;
      }
      if (scope === "shared") {
        await openSharedThread(postId);
      } else {
        if (!state.selectedCommunityId) return;
        await openCommunityThread(state.selectedCommunityId, postId);
      }
      if (shouldFocusReply) focusReplyComposer();
      break;
    }
    case "tap-resonance": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      await tapResonance(postId);
      break;
    }
    case "toggle-save-post": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      await toggleSavedPost(postId);
      break;
    }
    case "share-post": {
      const sscope = (target as HTMLElement).dataset["scope"];
      const spostId = (target as HTMLElement).dataset["postId"];
      if (!spostId) return;
      const spost = sscope === "shared" ? lookupSharedPost(spostId) : lookupCommunityPost(spostId);
      if (spost) sharePost(spost);
      break;
    }
    case "ask-friend": {
      const ascope = (target as HTMLElement).dataset["scope"];
      const apostId = (target as HTMLElement).dataset["postId"];
      if (!apostId) return;
      const apost = ascope === "shared" ? lookupSharedPost(apostId) : lookupCommunityPost(apostId);
      if (apost) handleAskFriend(apost);
      break;
    }
    case "toggle-expand": {
      const key = (target as HTMLElement).dataset["key"];
      if (!key) return;
      const expandEl = document.getElementById(`expand-${key}`) as HTMLElement | null;
      const btn = target as HTMLButtonElement;
      if (!expandEl) { render(); break; } // fallback
      if (state.expandedPostIds.has(key)) {
        // Collapse with animation
        state.expandedPostIds.delete(key);
        btn.textContent = "Read more";
        expandEl.style.overflow = "hidden";
        expandEl.style.maxHeight = `${expandEl.scrollHeight}px`;
        requestAnimationFrame(() => {
          expandEl.style.maxHeight = "5em";
          expandEl.addEventListener("transitionend", () => {
            expandEl.classList.add("clamped");
            expandEl.style.maxHeight = "";
            expandEl.style.overflow = "";
          }, { once: true });
        });
      } else {
        // Expand with animation
        state.expandedPostIds.add(key);
        btn.textContent = "Read less";
        expandEl.style.overflow = "hidden";
        expandEl.style.maxHeight = "5em";
        expandEl.classList.remove("clamped");
        const fullH = expandEl.scrollHeight;
        requestAnimationFrame(() => {
          expandEl.style.maxHeight = `${fullH}px`;
          expandEl.addEventListener("transitionend", () => {
            expandEl.style.maxHeight = "";
            expandEl.style.overflow = "";
          }, { once: true });
        });
      }
      break;
    }
    case "close-thread": {
      await closeThreadModal();
      break;
    }
    case "focus-reply": {
      setTimeout(() => {
        (document.querySelector("textarea[data-field='reply-text']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "reply-to": {
      const replyId = (target as HTMLElement).dataset["replyId"];
      if (!replyId) return;
      const _rtModal = document.querySelector<HTMLElement>(".modal");
      const _rtModalScroll = _rtModal?.scrollTop ?? 0;
      const _rtPageScroll = window.scrollY;
      state.replyingToId = replyId;
      render();
      requestAnimationFrame(() => {
        window.scrollTo({ top: _rtPageScroll, behavior: "instant" as ScrollBehavior });
        const newModal = document.querySelector<HTMLElement>(".modal");
        if (newModal) {
          newModal.scrollTop = _rtModalScroll;
          newModal.querySelector<HTMLTextAreaElement>("textarea[data-field='reply-text']")?.focus();
        }
      });
      break;
    }
    case "toggle-similar-thoughts": {
      await toggleInlineSimilarThoughts(target as HTMLElement);
      break;
    }
    case "open-similar-thought": {
      const thScope = (target as HTMLElement).dataset["scope"];
      const thPostId = (target as HTMLElement).dataset["postId"];
      const thGroupId = (target as HTMLElement).dataset["groupId"];
      if (!thScope || !thPostId) return;
      const openKey = similarThoughtOpenKey(thScope, thPostId, thGroupId || "");
      state.openingSimilarThoughtId = openKey;
      render();
      try {
        if (thScope === "sharedPost" || thScope === "shared") {
          await openSharedThread(thPostId);
        } else if ((thScope === "communityPost" || thScope === "community") && thGroupId) {
          await openCommunityThread(thGroupId, thPostId);
        }
      } finally {
        if (state.openingSimilarThoughtId === openKey) {
          state.openingSimilarThoughtId = null;
          render();
        }
      }
      break;
    }
    case "dismiss-resonance": {
      state.composerResonance = null;
      render();
      break;
    }
    case "continue-resonance": {
      state.composerResonance = null;
      state.composer.placeholder = "What keeps returning for you lately?";
      setActiveTab("write", true);
      render();
      setTimeout(() => {
        (document.querySelector("textarea[data-field='composer-text']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "share-resonance-match": {
      await shareResonanceMatch();
      break;
    }
    case "toggle-anchor-original": {
      if (state.dailyAnchor?.isTranslated) {
        state.dailyAnchor.showOriginal = !state.dailyAnchor.showOriginal;
        render();
      }
      break;
    }
    case "similar-thought-helpful": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      await saveSimilarThoughtFeedback(postId, "helpful");
      break;
    }
    case "similar-thought-hide": {
      const postId = (target as HTMLElement).dataset["postId"];
      if (!postId) return;
      await saveSimilarThoughtFeedback(postId, "hidden");
      break;
    }
    case "open-companion": {
      const noteId = (target as HTMLElement).dataset["noteId"];
      if (!noteId) return;
      await openCompanionSession(noteId);
      setTimeout(() => {
        (document.querySelector("textarea[data-field='companion-input']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "open-standalone-companion": {
      await openCompanionSession();
      if (state.activeTab === "companion" && state.companionHomeIntent !== "talk") await setCompanionIntent(state.companionHomeIntent);
      setTimeout(() => {
        (document.querySelector("textarea[data-field='companion-input']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "companion-starter-question": {
      // Empty Companion still asks about you: the tapped question becomes
      // the conversation's first words.
      const question = (target.getAttribute("data-question") || "").trim();
      await openCompanionSession(undefined, question || undefined);
      if (state.companionHomeIntent !== "talk") await setCompanionIntent(state.companionHomeIntent);
      setTimeout(() => {
        (document.querySelector("textarea[data-field='companion-input']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "scroll-directions": {
      // Edge arrows for the direction chips — pointer devices can't swipe
      // and the scrollbar is hidden, so the arrows do the moving.
      const row = document.querySelector<HTMLElement>(".discovery-directions");
      if (!row) break;
      const dir = Number((target as HTMLElement).dataset["dir"] || 1) || 1;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      row.scrollBy({
        left: dir * Math.max(row.clientWidth * 0.7, 180),
        behavior: reduceMotion ? "auto" : "smooth",
      });
      break;
    }
    case "companion-moment-page": {
      // The moment deck's dots — iOS momentPager parity without the drag.
      state.companionMomentPage = Number((target as HTMLElement).dataset["page"] || 0) || 0;
      render();
      break;
    }
    case "companion-not-now": {
      // "Not now" quietly acknowledges the lead pattern so the next one takes
      // the moment. Persisted locally — acknowledging is a device-level mood,
      // not account data.
      const patternId = (target as HTMLElement).dataset["patternId"] || "";
      if (!patternId) break;
      state.companionMomentPage = 0;
      // Move the id to the end of the list so repeat acknowledgements rotate
      // through patterns in least-recently-dismissed order.
      const ids = readAcknowledgedPatternIds().filter((id) => id !== patternId);
      ids.push(patternId);
      try {
        window.localStorage.setItem(COMPANION_ACK_STORAGE_KEY, JSON.stringify(ids));
      } catch {
        // Storage unavailable (private mode) — the moment simply stays.
      }
      render();
      break;
    }
    case "close-companion": {
      stopCompanionVoiceInput(false);
      await finishCompanionSession();
      break;
    }
    case "finish-without-memory": { closeCompanionSession(); break; }
    case "keep-talking": { keepTalking(); break; }
    case "select-companion-home-intent": {
      // iOS parity: the chips choose how Companion listens; the bar starts it.
      const intent = target.getAttribute("data-intent");
      if (intent === "talk" || intent === "deeper" || intent === "find_words") {
        state.companionHomeIntent = intent;
        render();
      }
      break;
    }
    case "send-companion-message": {
      if (companionVoiceActive) stopCompanionVoiceInput(false);
      await sendCompanionMessage();
      setTimeout(() => {
        (document.querySelector("textarea[data-field='companion-input']") as HTMLElement | null)?.focus();
      }, 0);
      break;
    }
    case "set-companion-mode": {
      const mode = (target as HTMLElement).dataset["mode"];
      if (mode === "reflect" || mode === "clarify" || mode === "ground" || mode === "pattern" || mode === "question") {
        setCompanionMode(mode);
      }
      break;
    }
    case "set-companion-intent": {
      const intent = (target as HTMLElement).dataset["intent"];
      if (intent === "talk" || intent === "deeper" || intent === "find_words") {
        await setCompanionIntent(intent);
      }
      break;
    }
    case "toggle-companion-voice": {
      toggleCompanionVoiceInput();
      break;
    }
    case "prepare-companion-memory": {
      await prepareCompanionMemory();
      break;
    }
    case "save-companion-memory": {
      await savePreparedCompanionMemory();
      break;
    }
    case "dismiss-companion-memory": {
      dismissCompanionMemoryOffer();
      break;
    }
    case "prepare-companion-journal": {
      await prepareCompanionJournal();
      break;
    }
    case "discard-companion-journal": {
      discardCompanionJournalDraft();
      break;
    }
    case "save-companion-journal": {
      await saveCompanionJournalDraft();
      break;
    }
    case "save-companion-guidance": {
      const guidance = getFieldValue("textarea[data-field='companion-guidance']", "");
      await updateCompanionPortraitGuidance(guidance);
      break;
    }
    case "delete-companion-memory": {
      const memoryId = (target as HTMLElement).dataset["memoryId"];
      if (!memoryId) return;
      if (await askConfirm("Delete this remembered understanding?")) {
        await deleteCompanionMemory(memoryId);
      }
      break;
    }
    case "delete-companion-portrait": {
      if (await askConfirm("Delete what Companion currently understands about you?")) {
        await deleteCompanionPortrait();
      }
      break;
    }
    case "cancel-reply-target": {
      const _crtModal = document.querySelector<HTMLElement>(".modal");
      const _crtModalScroll = _crtModal?.scrollTop ?? 0;
      const _crtPageScroll = window.scrollY;
      state.replyingToId = null;
      render();
      requestAnimationFrame(() => {
        window.scrollTo({ top: _crtPageScroll, behavior: "instant" as ScrollBehavior });
        const newModal = document.querySelector<HTMLElement>(".modal");
        if (newModal) newModal.scrollTop = _crtModalScroll;
      });
      break;
    }
    case "guest-sign-in": {
      state.guestMode = false;
      await closeThreadModal();
      render();
      break;
    }
    case "send-reply": {
      if (state.guestMode && !state.user) { state.guestMode = false; await closeThreadModal(); render(); break; }
      await createReply();
      break;
    }
    case "delete-reply": {
      const drId = (target as HTMLElement).dataset["replyId"];
      if (!drId) return;
      if (await askConfirm("Delete this response?")) {
        await deleteReply(drId);
      }
      break;
    }
    case "report-reply": {
      const rrId = (target as HTMLElement).dataset["replyId"];
      if (!rrId) return;
      await reportReply(rrId);
      break;
    }
    case "run-friend-search": {
      await searchUsers(state.friendSearch);
      break;
    }
    case "set-friends-tab": {
      const ftab = (target as HTMLElement).dataset["friendsTab"] as "friends" | "requests" | "discover";
      if (ftab) state.friendsTab = ftab;
      render();
      break;
    }
    case "open-public-profile": {
      const profileUid = (target as HTMLElement).dataset["uid"];
      if (!profileUid) return;
      if (!state.user) {
        // Profiles are readable only when signed in; say so instead of
        // letting the read fail into a misleading "Profile not found".
        showToast("Sign in to view profiles.");
        break;
      }
      await openPublicProfile(profileUid);
      break;
    }
    case "close-public-profile": {
      closePublicProfile();
      break;
    }
    case "send-friend-request": {
      const sfUid = (target as HTMLElement).dataset["uid"];
      if (!sfUid) return;
      await sendFriendRequest(sfUid);
      render();
      break;
    }
    case "accept-friend": {
      const afUid = (target as HTMLElement).dataset["uid"];
      if (!afUid) return;
      await acceptFriendRequest(afUid);
      render();
      break;
    }
    case "decline-friend": {
      const dfUid = (target as HTMLElement).dataset["uid"];
      if (!dfUid) return;
      await declineFriendRequest(dfUid);
      render();
      break;
    }
    case "remove-friend": {
      const rfUid = (target as HTMLElement).dataset["uid"];
      if (!rfUid) return;
      if (await askConfirm("Remove this friend?")) {
        await removeFriend(rfUid);
        render();
      }
      break;
    }
    case "join-by-code": {
      await joinByCode(state.joinCodeInput);
      state.communityOverlay = null;
      if (state.selectedCommunityId) {
        state.communitySubPage = "detail";
        state.communityDetailTab = "posts";
      }
      render();
      break;
    }
    case "community-nav": {
      const page = (target as HTMLElement).dataset["page"] as "browse" | "detail" | "settings";
      if (page) state.communitySubPage = page;
      render();
      break;
    }
    case "community-detail-tab": {
      const dtab = (target as HTMLElement).dataset["tab"] as "posts" | "members";
      if (dtab) state.communityDetailTab = dtab;
      render();
      break;
    }
    case "community-overlay": {
      const ov = (target as HTMLElement).dataset["overlay"] as "create" | "join";
      state.communityOverlay = ov || null;
      render();
      break;
    }
    case "close-community-overlay": {
      state.communityOverlay = null;
      render();
      break;
    }
    case "open-community": {
      const ocId = (target as HTMLElement).dataset["communityId"];
      if (!ocId) return;
      await openCommunity(ocId);
      state.communitySubPage = "detail";
      state.communityDetailTab = "posts";
      break;
    }
    case "join-community": {
      const jcId = (target as HTMLElement).dataset["communityId"];
      const jcGroup = state.communitiesPublic.find((g) => g.id === jcId);
      if (!jcGroup) return;
      await joinCommunity(jcGroup);
      if (state.selectedCommunityId) {
        state.communitySubPage = "detail";
        state.communityDetailTab = "posts";
      }
      render();
      break;
    }
    case "leave-community": {
      const lcId = (target as HTMLElement).dataset["communityId"];
      if (!lcId) return;
      if (await askConfirm("Leave this community?")) {
        await leaveCommunity(lcId);
        state.communitySubPage = "browse";
        render();
      }
      break;
    }
    case "delete-community": {
      const dcId = (target as HTMLElement).dataset["communityId"];
      const communityName = state.selectedCommunity?.name || "this community";
      if (!dcId) return;
      if (await askConfirm(`Delete ${communityName}? This permanently removes its posts and members.`)) {
        await deleteCommunity(dcId);
        state.communitySubPage = "browse";
        render();
      }
      break;
    }
    case "accept-invite": {
      const aiId = (target as HTMLElement).dataset["communityId"];
      if (!aiId) return;
      await acceptInvite(aiId);
      state.communitySubPage = "detail";
      state.communityDetailTab = "posts";
      render();
      break;
    }
    case "deny-invite": {
      const diId = (target as HTMLElement).dataset["communityId"];
      if (!diId) return;
      await denyInvite(diId);
      render();
      break;
    }
    case "reload-community": {
      if (state.selectedCommunityId) {
        await openCommunity(state.selectedCommunityId);
      }
      break;
    }
    case "set-community-sort": {
      const sortVal = (target as HTMLElement).dataset["sort"];
      state.communitySort = sortVal === "newest" ? "newest" : "active";
      if (state.selectedCommunityId) {
        await fetchCommunityPosts(state.selectedCommunityId);
      }
      render();
      break;
    }
    case "create-community-post": {
      if (!state.selectedCommunityId) {
        showToast("Open a community first.");
        break;
      }
      await createCommunityPost(state.selectedCommunityId, state.communityPostDraft.text, state.communityPostDraft.attribution);
      render();
      break;
    }
    case "delete-community-post": {
      if (!state.selectedCommunityId) return;
      const dcpId = (target as HTMLElement).dataset["postId"];
      if (!dcpId) return;
      if (await askConfirm("Delete this community post?")) {
        await deleteCommunityPost(state.selectedCommunityId, dcpId);
      }
      break;
    }
    case "invite-member": {
      if (!state.selectedCommunityId) {
        showToast("Open a community first.");
        break;
      }
      await inviteByUsername(state.selectedCommunityId, state.communityInviteSearch);
      break;
    }
    case "remove-community-member": {
      if (!state.selectedCommunityId) return;
      const memberUid = (target as HTMLElement).dataset["memberUid"];
      if (!memberUid) return;
      if (await askConfirm("Remove this member from the community?")) {
        await removeCommunityMember(state.selectedCommunityId, memberUid);
      }
      break;
    }
    case "save-note": {
      await saveNote();
      render();
      break;
    }
    case "edit-note": {
      const enId = (target as HTMLElement).dataset["noteId"];
      const enNote = state.notes.find((n) => n.id === enId);
      if (!enNote) return;
      state.noteDraft = { id: enNote.id, text: enNote.text };
      render();
      break;
    }
    case "cancel-note-edit": {
      state.noteDraft = { id: null, text: "" };
      render();
      break;
    }
    case "delete-note": {
      const dnId = (target as HTMLElement).dataset["noteId"];
      if (!dnId) return;
      if (await askConfirm("Delete this note?")) {
        await deleteNote(dnId);
      }
      break;
    }
    case "refresh-thinking-timeline": {
      await fetchThinkingTimeline({ refresh: true });
      break;
    }
    case "toggle-thinking-timeline": {
      state.thinkingTimeline.isExpanded = !state.thinkingTimeline.isExpanded;
      render();
      break;
    }
    case "open-thinking-pattern": {
      const patternId = (target as HTMLElement).dataset["patternId"];
      if (!patternId) return;
      state.thinkingTimeline.isExpanded = true;
      state.thinkingTimeline.selectedPatternId = patternId;
      render();
      break;
    }
    case "close-thinking-pattern": {
      state.thinkingTimeline.selectedPatternId = null;
      render();
      break;
    }
    case "refresh-activity": {
      await fetchActivity();
      render();
      break;
    }
    case "open-activity": {
      const oaId = (target as HTMLElement).dataset["activityId"];
      if (!oaId) return;
      await openActivity(oaId, setActiveTab);
      break;
    }
    case "toggle-pref": {
      const prefKey = (target as HTMLElement).dataset["prefKey"];
      if (!prefKey) return;
      await saveNotificationPreference(prefKey, (target as HTMLInputElement).checked);
      break;
    }
    case "toggle-notes-ai": {
      const enabled = (target as HTMLInputElement).checked;
      if (enabled && !state.aiPrefs.notesAIDisclosureAccepted) {
        const accepted = await askConfirm(
          "Enable private AI reflection for notes? Your note text is sent for analysis only — it is not stored in AI documents, and AI metadata stays private to your account."
        );
        if (!accepted) return;
        await saveAIPreferences({ notesAiEnabled: true, notesAIDisclosureAccepted: true });
      } else {
        await saveAIPreferences({ notesAiEnabled: enabled });
      }
      break;
    }
    case "setup-cross-platform-encryption": {
      const passphrase = getFieldValue("input[name='cross-platform-passphrase']", "");
      const confirmation = getFieldValue("input[name='cross-platform-confirm']", "");
      const rememberBrowser = Boolean(document.querySelector<HTMLInputElement>("input[name='cross-platform-remember']")?.checked);
      await setupCrossPlatformEncryption(passphrase, confirmation, rememberBrowser);
      render();
      break;
    }
    case "skip-passphrase-setup": {
      // Session-only defer for existing accounts — re-prompts next launch.
      state.passphraseSetupDismissed = true;
      render();
      break;
    }
    case "unlock-cross-platform-encryption": {
      const passphrase = getFieldValue("input[name='cross-platform-unlock']", "");
      const rememberBrowser = Boolean(document.querySelector<HTMLInputElement>("input[name='cross-platform-unlock-remember']")?.checked);
      await unlockCrossPlatformEncryption(passphrase, rememberBrowser);
      break;
    }
    case "change-cross-platform-passphrase": {
      const current = getFieldValue("input[name='cross-platform-current']", "");
      const next = getFieldValue("input[name='cross-platform-new']", "");
      const confirmation = getFieldValue("input[name='cross-platform-new-confirm']", "");
      const rememberBrowser = Boolean(document.querySelector<HTMLInputElement>("input[name='cross-platform-change-remember']")?.checked);
      await changeCrossPlatformEncryptionPassphrase(current, next, confirmation, rememberBrowser);
      break;
    }
    case "forget-cross-platform-unlock": {
      forgetRememberedCrossPlatformEncryption();
      break;
    }
    case "browse-as-guest": {
      state.guestMode = true;
      state.activeTab = "feed";
      render();
      void fetchPublicFeedAsGuest();
      break;
    }
    case "sign-out": {
      if (!state.user && state.guestMode) {
        state.guestMode = false;
        render();
        break;
      }
      await runSignOut();
      break;
    }
    case "delete-account": {
      await deleteAccountFlow();
      break;
    }
    case "confirm-yes": {
      const onConfirm = state.confirmModal?.onConfirm;
      state.confirmModal = null;
      render();
      onConfirm?.();
      break;
    }
    case "confirm-no": {
      const onCancel = state.confirmModal?.onCancel;
      state.confirmModal = null;
      render();
      onCancel?.();
      break;
    }
    default:
      break;
  }
}

// ── Field sync (input event) ──────────────────────────────────────────────
function handleFieldInput(field: string, target: HTMLElement): void {
  const value = (target as HTMLInputElement | HTMLTextAreaElement).value;
  switch (field) {
    case "composer-text":
      state.composer.text = value;
      if (target instanceof HTMLTextAreaElement) resizeComposerTextArea(target);
      break;
    case "composer-match-consent":
      state.composer.matchConsent = (target as HTMLInputElement).checked;
      break;
    case "match-excerpt":
      if (state.thoughtMatchExcerptDraft) {
        state.thoughtMatchExcerptDraft.excerpt = value;
        state.thoughtMatchExcerptError = null;
      }
      break;
    case "match-message":
      state.thoughtMatchInput = value;
      break;
    case "reply-text":
      state.replyText = value;
      break;
    case "reply-attribution":
      state.replyAttribution = value as "anonymous" | "username";
      break;
    case "friend-search":
      state.friendSearch = value;
      break;
    case "join-code-input":
      state.joinCodeInput = value;
      break;
    case "community-invite-username":
      state.communityInviteSearch = value;
      break;
    case "note-text":
      state.noteDraft.text = value;
      break;
    case "community-post-text":
      state.communityPostDraft.text = value;
      break;
    case "community-post-attribution":
      state.communityPostDraft.attribution = value as "anonymous" | "username";
      break;
    case "update-theme": selectMindTheme(value); break;
    case "update-circle-text": setCircleDraft(value); break;
    case "update-candidate": selectMindCandidate(value); break;
    case "update-excerpt-consent": void changeMindConsent((target as HTMLInputElement).checked, true); break;
    case "update-consent": void changeMindConsent((target as HTMLInputElement).checked); break;
    case "companion-input":
      if (state.companionSession) {
        state.companionSession.input = value;
      }
      break;
    case "companion-memory-summary":
      editCompanionMemory("summary", value); break;
    case "companion-memory-question":
      editCompanionMemory("nextQuestion", value); break;
    case "companion-journal":
      updateCompanionJournalDraft(value);
      break;
    case "composer-visibility":
      state.composer.visibility = value as "private" | "public" | "friends";
      if (state.composer.visibility === "private") {
        state.composer.attribution = "anonymous";
        state.composerResonance = null;
        state.composer.shareExpanded = false;
      }
      break;
    case "composer-attribution":
      state.composer.attribution = value as "anonymous" | "username";
      break;
    case "safe-review-text":
      if (state.composer.safeReview) state.composer.safeReview.safeText = value;
      break;
    default:
      break;
  }
}

// ── Segmented-control clicks (buttons acting as selects) ──────────────────
function handleFieldClick(field: string, target: HTMLElement): void {
  const value = target.dataset["value"];
  if (!value) return;
  switch (field) {
    case "composer-visibility":
      state.composer.visibility = value as "private" | "public" | "friends";
      if (state.composer.visibility === "private") {
        state.composer.attribution = "anonymous";
        state.composerResonance = null;
        state.composer.shareExpanded = false;
      }
      render();
      break;
    case "composer-attribution":
      state.composer.attribution = value as "anonymous" | "username";
      render();
      break;
    default:
      break;
  }
}

// ── Event delegation ──────────────────────────────────────────────────────
document.addEventListener("click", async (event) => {
  // Modal/backdrop click
  const et = event.target as HTMLElement | null;
  if (et?.classList.contains("modal-backdrop")) {
    await closeThreadModal();
    return;
  }
  if (et?.classList.contains("co-backdrop")) {
    await handleAction("close-community-overlay", et);
    return;
  }
  if (et?.classList.contains("profile-overlay")) {
    if (state.thinkingTimeline.selectedPatternId) {
      state.thinkingTimeline.selectedPatternId = null;
      render();
    } else {
      closePublicProfile();
    }
    return;
  }
  if (et?.classList.contains("companion-backdrop")) {
    if (closeUpdateOverlay()) return;
    stopCompanionVoiceInput(false);
    void finishCompanionSession();
    return;
  }
  if (et?.classList.contains("confirm-backdrop")) {
    const cancel = state.confirmModal?.onCancel;
    state.confirmModal = null;
    render();
    cancel?.();
    return;
  }

  const actionable = (event.target as Element).closest("[data-action]") as HTMLElement | null;
  const fieldable = (event.target as Element).closest("[data-field]") as HTMLElement | null;

  if (actionable instanceof HTMLAnchorElement) {
    event.preventDefault();
  }

  if (actionable?.dataset["action"]) {
    if (actionable instanceof HTMLAnchorElement) {
      event.preventDefault();
    }
    await handleAction(actionable.dataset["action"]!, actionable);
    return;
  }

  if (fieldable?.dataset["field"] && fieldable.tagName === "BUTTON") {
    handleFieldClick(fieldable.dataset["field"]!, fieldable);
  }
});

document.addEventListener("input", (event) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  const field = target.dataset["field"];
  if (!field) return;
  handleFieldInput(field, target);
  if (target instanceof HTMLTextAreaElement && field === "companion-input") {
    target.style.height = "auto";
    target.style.height = `${Math.min(target.scrollHeight, 180)}px`;
  }
  if (field === "composer-match-consent") {
    render();
  } else if (field === "match-excerpt" || field === "match-message" || field === "companion-memory-summary" || field === "update-circle-text") {
    // Re-render only when emptiness flips (that's when send buttons
    // enable/disable) — never on every keystroke. Full-DOM rebuilds
    // under a moving caret are the instability users feel.
    const value = (target as HTMLTextAreaElement).value || "";
    const isEmpty = String(!value.trim());
    if (target.dataset["wasEmpty"] !== isEmpty) {
      target.dataset["wasEmpty"] = isEmpty;
      render();
    }
  }
});

document.addEventListener("submit", async (event) => {
  const form = event.target as HTMLFormElement | null;
  if (!form) return;
  event.preventDefault();

  const fd = new FormData(form);
  const str = (name: string) => String(fd.get(name) || "").trim();

  if (form.id === "email-auth-form") {
    const email = str("email");
    const password = str("password");
    if (!email || !password) { showToast("Enter email and password."); return; }
    await signInWithEmail(email, password);
    return;
  }

  if (form.id === "username-form") {
    await saveUsername(str("username"));
    return;
  }

  if (form.id === "community-create-form") {
    const created = await createCommunity({
      name: str("name"),
      purpose: str("purpose"),
      visibility: str("visibility") as "public" | "private",
    });
    if (created) {
      state.communityOverlay = null;
      state.communitySubPage = "detail";
    }
    render();
    return;
  }

  if (form.id === "profile-settings-form") {
    const avatarFile = fd.get("avatar") instanceof File && (fd.get("avatar") as File).size > 0
      ? fd.get("avatar") as File
      : null;
    await saveProfileSettings({
      username: str("username"),
      displayName: str("displayName"),
      bio: str("bio"),
      avatarFile,
      removeAvatar: fd.get("removeAvatar") === "on",
    });
    render();
    return;
  }

  if (form.id === "join-code-settings-form") {
    await updateJoinCodeSettings({
      enabled: fd.get("enabled") === "on",
      code: str("code"),
      expiresAt: str("expiresAt"),
    });
    render();
    return;
  }
});

// ── OAuth redirect result ─────────────────────────────────────────────────
getRedirectResult(auth).catch((error) => {
  console.warn("OAuth redirect result failed", {
    code: (error as { code?: string })?.code,
    message: (error as Error)?.message,
  });
  const customData = (error as { customData?: { providerId?: string } })?.customData;
  const providerName = String(customData?.providerId || "").includes("apple")
    ? "Apple"
    : "Google";
  logAuthDiagnostics(error, providerName);
  showToast(authFailureMessage(error, providerName));
});

// ── Deep links ──────────────────────────────────────────────────────────────
// A thought's canonical URL is /t/{id}; ?post={id}[&reply={id}] is the app's
// own form. The server serves the app shell at /t/{id} (with that thought's
// link-preview metadata), and the app opens the thread in place — like a
// tweet URL, the app IS the page. This runs for signed-out visitors too: the
// person most likely to open a shared link doesn't have an account yet.
function deepLinkedThreadFromLocation(): { postId: string; replyId: string | null } | null {
  const params = new URLSearchParams(window.location.search);
  const thoughtPath = window.location.pathname.match(/^\/t\/([^/?#]+)/);
  const postId = params.get("post") || (thoughtPath ? decodeURIComponent(thoughtPath[1]) : null);
  if (!postId) return null;
  return { postId, replyId: params.get("reply") };
}

async function openDeepLinkedThreadIfAny(): Promise<boolean> {
  const link = deepLinkedThreadFromLocation();
  if (!link) return false;
  // A /t/{id} URL stays in the address bar — it IS the thought's address.
  // Only the ?post= form normalizes the path to /feed.
  const fromCanonicalPath = /^\/t\//.test(window.location.pathname);
  setActiveTab("feed", !fromCanonicalPath);
  render();
  await openSharedThread(link.postId, link.replyId);
  return true;
}

// ── Auth state ─────────────────────────────────────────────────────────────
onAuthStateChanged(auth, async (user) => {
  state.user = user;
  state.loadingAuth = false;

  if (!user) {
    stopCompanionVoiceInput(false);
    stopThoughtMatching();
    state.profileResolved = false;
    state.profile = null;
    state.notificationPrefs = null;
    state.feedPosts = [];
    state.feedInitialLoadDone = false;
    state.savedPostIds = new Set();
    state.feedFilter = "all";
    state.feedFilterUserSet = false;    // reset so next sign-in gets smart default
    state.selectedDiscoveryDirection = null;
    state.discoveryDirections = [];
    state.discoveryTagsByPostId = {};
    state.discoveryShelfPostIds = null;
    state.sharedThoughtNotice = null;
    state.savedPostsById = {};
    resetPortraitState();
    resetPortraitMatching();
    state.discoveryRankedIds = [];
    state.discoveryRelatedIds = [];
    state.feedDisplayOrder = [];
    state.feedPendingNewIds = [];
    state.feedRevealAnimateIds = new Set();
    state.feedSectionIds = {
      all: { active: [], newThoughts: [], discovery: [] },
      friends: { active: [], newThoughts: [], discovery: [] },
    };
    state.friends = [];
    state.friendSuggestions = [];
    state.friendSearchResults = [];
    state.communitiesMy = [];
    state.communitiesPublic = [];
    state.communitiesInvites = [];
    state.selectedCommunityId = null;
    state.selectedCommunity = null;
    state.selectedCommunityPosts = [];
    state.selectedCommunityMembers = [];
    state.communitySubPage = "browse";
    state.communityOverlay = null;
    state.composer = {
      text: "",
      visibility: "private",
      attribution: "anonymous",
      placeholder: "",
      matchConsent: true,
      isListening: false,
      shareExpanded: false,
      safeReview: null,
    };
    state.composerResonance = null;
    state.notes = [];
    state.companionSession = null;
    state.companionContext = {
      memories: [],
      portrait: null,
      loaded: false,
      loading: false,
      error: null,
    };
    state.noteEncryption = {
      hasEnvelope: false,
      unlocked: false,
      remembered: false,
      preferredMode: "cross_platform",
      hasLegacyIOSNotes: false,
    };
    state.activity = [];
    state.threadModal = null;
    state.threadReplies = [];
    state.expandedPostIds = new Set();
    state.expandedSimilarThoughtIds = new Set();
    state.openingSimilarThoughtId = null;
    state.inlineSimilarThoughtLoading = new Set();
    // A signed-out visitor should land on something readable, never on a
    // locked gate. Public Discovery is the front door; sign-in is a choice
    // they make from there, not a wall they hit first.
    state.guestMode = true;
    setActiveTab("feed", false);
    render();
    if (state.guestMode) {
      void fetchPublicFeedAsGuest();
    }
    void openDeepLinkedThreadIfAny();
    if (pendingNativeUrl) {
      const url = pendingNativeUrl;
      pendingNativeUrl = null;
      void handleIncomingUrl(url);
    }
    return;
  }

  state.profileResolved = false;
  setActiveTab(tabFromPath(window.location.pathname), false);
  render();
  await hydrateAppData();
  void registerNativePushIfPossible();
  render();

  const params = new URLSearchParams(window.location.search);
  const deepCode = params.get("code");
  await openDeepLinkedThreadIfAny();
  // Your portrait and portrait matching resolve their gates at sign-in so
  // the You hub can show the right rows without a tab visit first.
  void refreshPortraitAvailability().then(() => refreshPortraitConsent()).then(() => render());
  void startPortraitMatching();
  if (deepCode) {
    // Retired surface — see the invite-code handler above.
    setActiveTab("feed", true);
    render();
    showToast("Communities have been retired. Welcome to Discovery.");
  }
  if (pendingNativeUrl) {
    const url = pendingNativeUrl;
    pendingNativeUrl = null;
    await handleIncomingUrl(url);
  }
});

// ── Pop state (browser back/forward) ─────────────────────────────────────
window.addEventListener("popstate", () => {
  if (isOverlayOpen()) {
    // Close the topmost overlay instead of navigating away — same
    // reverse-depth-order priority as installNativeBackButtonHandling().
    // Don't touch overlayHistoryPushed here — syncOverlayHistory() (called
    // from the render() below) is the single place that clears it, so it
    // can tell this was a Back-triggered close and skip an extra
    // history.back() that would otherwise skip past the page the user
    // meant to land on.
    overlayHistoryPushed = false; // Back already consumed the overlay entry.
    suppressHistoryPopOnClose = false;
    if (closeUpdateOverlay()) return;
    if (state.confirmModal) {
      const cancel = state.confirmModal.onCancel;
      state.confirmModal = null;
      render();
      cancel?.();
      return;
    }
    if (state.thinkingTimeline.selectedPatternId) {
      state.thinkingTimeline.selectedPatternId = null;
      render();
      return;
    }
    if (state.companionSession) {
      stopCompanionVoiceInput(false);
      void finishCompanionSession();
      return;
    }
    if (state.communityOverlay) {
      state.communityOverlay = null;
      render();
      return;
    }
    if (state.publicProfile) {
      closePublicProfile();
      return;
    }
    if (state.threadModal) {
      void closeThreadModal();
      return;
    }
    suppressHistoryPopOnClose = false; // isOverlayOpen() was true but nothing matched — shouldn't happen
  }
  setActiveTab(tabFromPath(window.location.pathname), false);
  render();
});

// ── Initial render ────────────────────────────────────────────────────────
installForegroundRepaintFix();   // runs on all platforms, not just native
installNativeAppChrome();
installNativeDeepLinkHandling();
installNativeBackButtonHandling();
render();

initializeCompanionUpdate(render);

document.addEventListener("keydown", event => {
  if (event.key === "Escape") {
    if (closeUpdateOverlay()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (state.companionSession) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (state.companionSession.finishing) keepTalking();
      else void finishCompanionSession();
      return;
    }
  }
  // ⌘/Ctrl+Return sends, as on iPhone and iPad with a keyboard.
  if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.isComposing) {
    const field = (event.target as HTMLElement | null)?.getAttribute?.("data-field");
    const action = field === "companion-input" ? "send-companion-message" : field === "composer-text" ? "create-post" : "";
    const button = action ? document.querySelector<HTMLButtonElement>(`[data-action='${action}']`) : null;
    if (button && !button.disabled) { event.preventDefault(); button.click(); }
    return;
  }
  if (event.key !== "Tab") return;
  const dialog = document.querySelector<HTMLElement>(".companion-backdrop:last-of-type [role=dialog], .companion-backdrop:last-of-type");
  if (!dialog) return;
  const items = Array.from(dialog.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href]"));
  if (!items.length) return;
  const first = items[0], last = items[items.length - 1];
  if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
}, true);
